<template>
  <v-container fluid class="container">
    <Breadcrumbs :items="breadcrumbs" />

    <v-toolbar flat>
      <ejs-dropdownlist
        id="dropdownlist"
        ref="dropdown"
        :dataSource="dropdownDataSource"
        :change="onReportDropdownChange"
        v-model="reportDropdown"
        placeholder="Report Type"
        width="250"
      />
      <v-spacer />
      <v-btn color="primary" @click="excelDownloadClick">
        <v-icon left>mdi-file-excel</v-icon>
        Export to Excel
      </v-btn>
    </v-toolbar>

    <v-spacer />

    <ejs-grid
      ref="grid"
      v-if="items.length && periods.length"
      :key="`grid-${reportDropdownValue}`"
      :dataSource="items"
      :filterSettings="{type: 'Menu'}"
      :allowFiltering='true'
      :allowSorting="true"
      :sortSettings="sortOptions"
      :height="560"
      :allowExcelExport="true"
      :allowTextWrap="true"
      :excelHeaderQueryCellInfo="excelHeader"
      :excelQueryCellInfo="excelQueryCellInfo"
    >
      <e-columns>
        <!-- Static columns -->
        <e-column field="id" headerText="ID" width="200" />
        <e-column field="line" headerText="Line" width="200" />
        <e-column field="fundingType" headerText="Existing / New" width="140" />
        <e-column field="currency" headerText="Currency" width="100" />
        <e-column field="reference_rate" headerText="Reference rate" width="140" />
        <e-column field="finrep_pl" headerText="Finrep PL" width="120" />
        <e-column field="finrep_pla" headerText="Finrep PLA" width="120" />
        <e-column field="maturity" headerText="Maturity" width="110" textAlign="Right" />
        <e-column field="repayment_id" headerText="Repayment" width="130" />
        <e-column field="fee" headerText="Fee" width="100" format="p4" textAlign="Right" />
        <e-column field="fee_accrual_id" headerText="Fee accrual" width="140" />
        <e-column field="placement_period" headerText="Period when the repayment starts" width="210" />

        <!-- Dynamic column groups like in SalesPlanningNew -->
        <e-column textAlign="center" headerText="Principal" :allowSorting="false" :columns="bookValueColumns" />
        <e-column textAlign="center" headerText="Interest rate" :allowSorting="false" :columns="interestRateColumns" />
        <e-column textAlign="center" headerText="Interest expense" :allowSorting="false" :columns="interestColumns" />
        <e-column textAlign="center" headerText="Fee expense" :allowSorting="false" :columns="feeColumns" />
      </e-columns>
    </ejs-grid>

    <Snackbar :text="notificationText" :type="notificationType" />

    <v-overlay :value="loader" :z-index="1000">
      <v-progress-circular indeterminate size="64" />
    </v-overlay>

  </v-container>
</template>
<script>
import Vue from 'vue';
import { mapGetters, mapState } from 'vuex';
import {
  GridPlugin, ExcelExport, Filter, Sort,
} from '@syncfusion/ej2-vue-grids';
import Breadcrumbs from '@/components/Breadcrumbs';
import Snackbar from '@/components/Snackbar';
import api from '@/api/lbt-api';

Vue.use(GridPlugin);

export default {
  name: 'SummaryDetailPositions',
  components: { Breadcrumbs, Snackbar },
  data() {
    return {
      notificationText: '',
      notificationType: null,
      loader: false,
      periods: [],
      items: [],
      reportDropdown: 'Funding analytics',
      reportDropdownValue: 'funding_balance_sheet',
      dropdownDataSource: ['Funding analytics'],
      // currencyDropdown: 'LCY',
      // currencyDropdownValue: 'lcy',
      // currencyDropdownDataSource: ['LCY', 'EUR'],
      breadcrumbs: [
        { translationKey: 'breadcrumbs_plannings', route: '/plannings', disabled: false },
        { translationKey: 'detail_positions', disabled: true },
      ],
      // Dynamic column arrays like in SalesPlanningNew
      bookValueColumns: [],
      interestRateColumns: [],
      interestColumns: [],
      feeColumns: [],
      sortOptions: { columns: [{ field: 'id', direction: 'Ascending' }] },
    };
  },
  computed: {
    ...mapGetters('translations', ['translate']),
    ...mapState('plannings', ['activePlanning']),
  },
  created() {
    this.fetchData();
  },
  methods: {
    async fetchData() {
      // console.log('fetchData: start');
      this.loader = true;
      try {
        const payload = await api.getSummaryDetailPositionsForPlanning(
          this.$route.params.planningId,
          this.reportDropdownValue,
          'eur',
          // this.currencyDropdownValue,
        );
        // console.log('API payload', payload);
        this.periods = payload.periods || [];

        // Transform data to flat structure
        const transformedItems = (payload.rows || []).map((item) => {
          const transformedItem = {
            id: item.id,
            line: item.line,
            fundingType: item.fundingType,
            currency: item.currency,
            reference_rate: item.reference_rate,
            finrep_pl: item.finrep_pl,
            finrep_pla: item.finrep_pla,
            maturity: item.maturity,
            repayment_id: item.repayment_id,
            fee: item.fee,
            fee_accrual_id: item.fee_accrual_id,
            placement_period: item.placement_period,
          };

          // Add period values as flat properties
          if (item.book_values && Array.isArray(item.book_values)) {
            item.book_values.forEach((value, index) => {
              transformedItem[`book_${index}`] = value;
            });
          }

          if (item.interest_values && Array.isArray(item.interest_values)) {
            item.interest_values.forEach((value, index) => {
              transformedItem[`interest_${index}`] = value;
            });
          }

          if (item.fee_values && Array.isArray(item.fee_values)) {
            item.fee_values.forEach((value, index) => {
              transformedItem[`feeexp_${index}`] = value;
            });
          }

          if (item.rate_values && Array.isArray(item.rate_values)) {
            item.rate_values.forEach((value, index) => {
              transformedItem[`rate_${index}`] = value;
            });
          }

          return transformedItem;
        });

        this.items = transformedItems;

        // Build dynamic columns after data is ready
        this.buildBookValueColumns();
        this.buildInterestRateColumns();
        this.buildInterestColumns();
        this.buildFeeColumns();

        // Force grid refresh
        this.$nextTick(() => {
          if (this.$refs.grid && this.$refs.grid.ej2Instances) {
            const grid = this.$refs.grid.ej2Instances[0];
            if (grid && grid.refresh) {
              // console.log('Refreshing Grid...');
              grid.refresh();
            }
          }
        });
      } catch (e) {
        this.notificationText = e.message || 'Failed to load data';
        this.notificationType = 'error';
        setTimeout(() => { this.notificationText = null; }, 3000);
      } finally {
        this.loader = false;
      }
    },

    // Build book value columns dynamically
    buildBookValueColumns() {
      this.bookValueColumns = [];
      this.periods.forEach((period, index) => {
        const colDef = {
          field: `book_${index}`,
          headerText: index.toString(),
          headerValueAccessor: this.lookupPeriodHeader,
          width: 80,
          format: 'n2',
          textAlign: 'Right',
          allowSorting: false,
          allowFiltering: false,
        };
        if (index === 0) {
          colDef.customAttributes = { class: 'group-1-first' };
        } else {
          colDef.customAttributes = { class: 'group-1' };
        }
        this.bookValueColumns.push(colDef);
      });
    },

    // Build interest rate columns dynamically
    buildInterestRateColumns() {
      this.interestRateColumns = [];
      this.periods.forEach((period, index) => {
        const colDef = {
          field: `rate_${index}`,
          headerText: index.toString(),
          headerValueAccessor: this.lookupPeriodHeader,
          width: 80,
          format: 'p2',
          textAlign: 'Right',
          allowSorting: false,
          allowFiltering: false,
        };
        if (index === 0) {
          colDef.customAttributes = { class: 'group-2-first' };
        } else {
          colDef.customAttributes = { class: 'group-2' };
        }
        this.interestRateColumns.push(colDef);
      });
    },

    // Build interest columns dynamically
    buildInterestColumns() {
      this.interestColumns = [];
      this.periods.forEach((period, index) => {
        const colDef = {
          field: `interest_${index}`,
          headerText: index.toString(),
          headerValueAccessor: this.lookupPeriodHeader,
          width: 80,
          format: 'n2',
          textAlign: 'Right',
          allowSorting: false,
          allowFiltering: false,
        };
        if (index === 0) {
          colDef.customAttributes = { class: 'group-3-first' };
        } else {
          colDef.customAttributes = { class: 'group-3' };
        }
        this.interestColumns.push(colDef);
      });
    },

    // Build fee expense columns dynamically
    buildFeeColumns() {
      this.feeColumns = [];
      this.periods.forEach((period, index) => {
        const colDef = {
          field: `feeexp_${index}`,
          headerText: index.toString(),
          headerValueAccessor: this.lookupPeriodHeader,
          width: 80,
          format: 'n2',
          textAlign: 'Right',
          allowSorting: false,
          allowFiltering: false,
        };
        if (index === 0) {
          colDef.customAttributes = { class: 'group-4-first' };
        } else {
          colDef.customAttributes = { class: 'group-4' };
        }
        this.feeColumns.push(colDef);
      });
    },

    // Header value accessor like in SalesPlanningNew
    lookupPeriodHeader(idx) {
      return this.periods[Number(idx)] || '';
    },

    onReportDropdownChange({ value }) {
      this.reportDropdownValue = value === 'Funding balance sheet analytics' ? 'funding_balance_sheet' : 'funding_pl';
      this.fetchData();
    },
    // onCurrencyDropdownChange({ value }) {
    //  this.currencyDropdownValue = value.toLowerCase();
    //  this.fetchData();
    // },
    async excelDownloadClick() {
      const grid = document.getElementsByClassName('e-grid')[0].ej2_instances[0];
      if (!grid) {
        // console.error('Grid not ready');
        return;
      }

      const suffixByHeader = {
        Principal: '_P',
        'Interest rate': '_IR',
        'Interest expense': '_IE',
        'Fee expense': '_FE',
      };

      // Flatten stacked headers in place so Excel gets a single header row
      // (no "Principal / Interest rate / Interest expense / Fee expense" row)
      // and period columns are suffixed with _P / _IR / _IE / _FE. We re-use the original
      // Column instances so data extraction keeps working, and restore the
      // grid structure in `finally`.
      const originalColumns = grid.columns;
      const flatColumns = [];
      const headerTextSnapshots = [];

      originalColumns.forEach((col) => {
        if (col.columns && col.columns.length) {
          const suffix = suffixByHeader[col.headerText] || '';
          col.columns.forEach((child) => {
            const m = String(child.field || '').match(/_(\d+)$/);
            const idx = m ? parseInt(m[1], 10) : 0;
            const periodLabel = this.periods[idx] || child.headerText || '';
            headerTextSnapshots.push({ column: child, headerText: child.headerText });
            // eslint-disable-next-line no-param-reassign
            child.headerText = `${periodLabel}${suffix}`;
            flatColumns.push(child);
          });
        } else {
          flatColumns.push(col);
        }
      });

      grid.columns = flatColumns;
      try {
        await grid.excelExport({
          exportType: 'CurrentPage',
          fileName: `Summary Detail Positions - ${this.reportDropdown} - Planning - ${this.activePlanning.year}-${this.activePlanning.month}.xlsx`,
        });
      } finally {
        headerTextSnapshots.forEach((snap) => {
          // eslint-disable-next-line no-param-reassign
          snap.column.headerText = snap.headerText;
        });
        grid.columns = originalColumns;
        if (typeof grid.refreshColumns === 'function') {
          grid.refreshColumns();
        }
      }
    },
    excelHeader(args) {
      const { cell } = args;
      const col = args.gridCell && args.gridCell.column;
      if (col && col.field === 'placement_period') {
        cell.value = 'Period when the repayment starts';
      }
      if (col && col.field && (col.field.startsWith('book_') || col.field.startsWith('rate_') || col.field.startsWith('interest_') || col.field.startsWith('feeexp_'))) {
        if (typeof cell.value === 'string' && /^\d+$/.test(cell.value)) {
          cell.value = this.lookupPeriodHeader(cell.value);
        }
      }
      this.applyExcelGroupStyle(args, true);
    },
    getExcelGroupStyle(column) {
      if (!column) {
        return null;
      }

      if (column.customAttributes && column.customAttributes.class) {
        return column.customAttributes.class;
      }

      const headerGroups = {
        Principal: 'group-1-first',
        'Interest rate': 'group-2-first',
        'Interest expense': 'group-3-first',
        'Fee expense': 'group-4-first',
      };
      return headerGroups[column.headerText] || null;
    },
    applyExcelGroupStyle(args, isHeader = false) {
      const col = args.column || (args.gridCell && args.gridCell.column);
      const className = this.getExcelGroupStyle(col);
      if (!className) {
        return;
      }

      const groupColors = {
        'group-1': { normal: '#fff9f5', alt: '#fff5eb' },
        'group-1-first': { normal: '#fff9f5', alt: '#fff5eb', border: true },
        'group-2': { normal: '#f5fdf5', alt: '#edfaed' },
        'group-2-first': { normal: '#f5fdf5', alt: '#edfaed', border: true },
        'group-3': { normal: '#fff9f5', alt: '#fff5eb' },
        'group-3-first': { normal: '#fff9f5', alt: '#fff5eb', border: true },
        'group-4': { normal: '#f5fdf5', alt: '#edfaed' },
        'group-4-first': { normal: '#f5fdf5', alt: '#edfaed', border: true },
      };

      const colors = groupColors[className];
      if (!colors) {
        return;
      }

      const rowIndex = args.data && typeof args.data.index === 'number' ? args.data.index : 0;
      const bgColor = !isHeader && rowIndex % 2 === 1 ? colors.alt : colors.normal;

      // eslint-disable-next-line no-param-reassign
      args.style = {
        ...args.style,
        backColor: bgColor,
      };

      if (colors.border) {
        // eslint-disable-next-line no-param-reassign
        args.style.borders = {
          color: '#FFFFFF',
          lineStyle: 'none',
        };
        // eslint-disable-next-line no-param-reassign
        args.style.leftBorder = {
          color: '#1976d2',
          lineStyle: 'thick',
        };
      }
    },
    excelQueryCellInfo(args) {
      this.applyExcelGroupStyle(args);
    },
  },
  provide: { grid: [ExcelExport, Filter, Sort] },
};
</script>
<style>
@import '~@syncfusion/ej2-vue-treegrid/styles/material.css';

.e-grid .e-headercell.highlight-cell,
.highlight-cell {
  background: #eeeeee;
}

.e-valid-input {
  margin-right: 10px;
}

.e-headercelldiv {
  text-align: center !important;
}

/* Column group dividers - add thicker borders between groups */
.e-grid .e-stackedheadercell {
  border-left: 3px solid #1976d2 !important;
  background: linear-gradient(to bottom, #e3f2fd 0%, #bbdefb 100%);
  font-weight: 600;
}

/* First column group doesn't need left border */
.e-grid .e-stackedheadercell:first-of-type {
  border-left: none !important;
}

/* Alternating background colors for column groups */
.e-grid .e-stackedheadercell:nth-child(odd) {
  background: linear-gradient(to bottom, #fff3e0 0%, #ffe0b2 100%);
}

.e-grid .e-stackedheadercell:nth-child(even) {
  background: linear-gradient(to bottom, #e8f5e9 0%, #c8e6c9 100%);
}

/* Apply dividers to first column of each group */
.e-grid .e-rowcell.group-1-first,
.e-grid .e-rowcell.group-2-first,
.e-grid .e-rowcell.group-3-first,
.e-grid .e-rowcell.group-4-first {
  border-left: 3px solid #1976d2 !important;
}

/* Apply background colors to all columns in each group */
/* Group 1 - Principal */
.e-grid .e-row .e-rowcell.group-1,
.e-grid .e-row .e-rowcell.group-1-first {
  background-color: #fff9f5;
}
.e-grid .e-altrow .e-rowcell.group-1,
.e-grid .e-altrow .e-rowcell.group-1-first {
  background-color: #fff5eb;
}

/* Group 2 - Interest rate */
.e-grid .e-row .e-rowcell.group-2,
.e-grid .e-row .e-rowcell.group-2-first {
  background-color: #f5fdf5;
}
.e-grid .e-altrow .e-rowcell.group-2,
.e-grid .e-altrow .e-rowcell.group-2-first {
  background-color: #edfaed;
}

/* Group 3 - Interest expense */
.e-grid .e-row .e-rowcell.group-3,
.e-grid .e-row .e-rowcell.group-3-first {
  background-color: #fff9f5;
}
.e-grid .e-altrow .e-rowcell.group-3,
.e-grid .e-altrow .e-rowcell.group-3-first {
  background-color: #fff5eb;
}

/* Group 4 - Fee expense */
.e-grid .e-row .e-rowcell.group-4,
.e-grid .e-row .e-rowcell.group-4-first {
  background-color: #f5fdf5;
}
.e-grid .e-altrow .e-rowcell.group-4,
.e-grid .e-altrow .e-rowcell.group-4-first {
  background-color: #edfaed;
}
</style>
